import React, { useContext, useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { UserContext } from "context/user";
import { MapActions, MapContext } from "context/map";
import usePremium from "hooks/usePremium";
import useClearPopUps from "hooks/useClearPopUps";
import { getFarm, getFields } from "api/Farm";
import { Polygon, Ruler, Trash } from "phosphor-react";
import Dropdown from "../DropdownMeasure";
import PremiumBar from "../PremiumBar";
import ViewFarm from "../ViewFarm";
import ManageFarm from "../ManageFarm";
import ZoneFarm from "../ZoneFarm";
import EditZoneFarm from "../EditZoneFarm";
import MapOption from "../MapOption";
import Map from "../Map";
import MapInspect from "../MapInspect";
import "./index.scss";

const MappingTool = () => {
  const lastSelectedSourceRef = useRef(null);
  const { isPremium } = usePremium();
  const { farmId } = useParams();
  const { token, profile } = useContext(UserContext);
  const { state, dispatch } = useContext(MapContext);
  const {
    mode,
    farm,
    drawerOpen,
    draw,
    measureMode,
    exportMode,
    selectedLayer,
  } = state;
  const clearPopUps = useClearPopUps();

  const fetchData = useCallback(async () => {
    if (!token) return;

    const farmData = await getFarm(farmId, token);
    const fieldsData = await getFields(farmId, token);

    dispatch({
      type: MapActions.SET_DATA,
      value: { farm: farmData, fields: fieldsData.features },
    });
  }, [dispatch, farmId, token]);

  const mapWrapperClassName = () => {
    let className = "map-wrapper";

    if (isPremium) {
      className += " -premium";
    }

    if (mode === "manage") {
      className += " -edit";
    } else if (mode === "zoning" || mode === "edit-zone") {
      className += " -zoning";
    } else if (mode === "book-em") {
      className += " -book-em";
    }

    if (drawerOpen) {
      className += " -drawer-open";
    }

    return className;
  };

  useEffect(() => {
    if (farm) {
      dispatch({ type: MapActions.SET_VIEW_MODE });

      return;
    }

    fetchData();
  }, [dispatch, farm, fetchData]);

  const enableMeasureLine = useCallback(() => {
    draw.changeMode("draw_line_string");
    if (measureMode === "measure_line") {
      localStorage.removeItem("isMeasuring");
      dispatch({ type: MapActions.SET_MEASURE_MODE, value: "" });
    } else {
      localStorage.setItem("isMeasuring", "1");
      dispatch({ type: MapActions.SET_MEASURE_MODE, value: "measure_line" });
    }
  }, [dispatch, draw, measureMode]);

  const enableMeasureAreaTool = useCallback(() => {
    draw.changeMode("draw_polygon");
    if (measureMode === "measure_area") {
      localStorage.removeItem("isMeasuring");
      dispatch({ type: MapActions.SET_MEASURE_MODE, value: "" });
    } else {
      dispatch({ type: MapActions.SET_MEASURE_MODE, value: "measure_area" });
      localStorage.setItem("isMeasuring", "1");
    }
  }, [dispatch, draw, measureMode]);

  const deleteSelectedMeasuredArea = useCallback(() => {
    const selectedId = draw.getSelectedIds();
    if (selectedId.length) {
      dispatch({ type: MapActions.SET_MEASURE_MODE, value: "measure_delete" });
      selectedId.forEach((element) => {
        draw.delete(element);
        clearPopUps(`pop-${element}`);
      });
    }
  }, [draw, dispatch, clearPopUps]);

  if (!profile || !farm) return null;

  const actions = [
    {
      id: "measure-line-tool",
      handleClick: () => enableMeasureLine(),
      children: (
        <Ruler
          size={20}
          color={measureMode === "measure_line" ? "#09ba51" : "#8a8e99"}
        />
      ),
      customClass: `button measure-line-tool ${
        measureMode === "measure_line" ? "active" : ""
      }`,
      disabled: mode === "manage",
    },
    {
      id: "measure-area-tool",
      handleClick: () => enableMeasureAreaTool(),
      children: (
        <Polygon
          size={20}
          color={measureMode === "measure_area" ? "#09ba51" : "#8a8e99"}
        />
      ),
      customClass: `button measure-area-tool ${
        measureMode === "measure_area" ? "active" : ""
      }`,
      disabled: mode === "manage",
    },
    {
      id: "measure-area-delete",
      handleClick: () => deleteSelectedMeasuredArea(),
      children: <Trash size={20} color="#8a8e99" />,
      disabled: mode === "manage",
    },
  ];

  return (
    <div className={mapWrapperClassName()} id="map-wrapper">
      {!isPremium && <PremiumBar />}
      {mode === "view" && (
        <ViewFarm lastSelectedSourceRef={lastSelectedSourceRef} />
      )}
      {mode === "zoning" && <ZoneFarm />}
      {mode === "edit-zone" && <EditZoneFarm />}
      {mode === "manage" && <ManageFarm />}
      {mode !== "zoning" && <MapOption />}
      {selectedLayer === "rice_harvest_prediction" && <MapInspect />}
      {mode !== "manage" && (
        <div
          className="measure-tools"
          style={exportMode ? { display: "none" } : {}}
        >
          <Dropdown
            disabled={mode === "manage"}
            buttonLabel="M"
            data={actions}
          />
        </div>
      )}
      <Map lastSelectedSourceRef={lastSelectedSourceRef} />
    </div>
  );
};

export default MappingTool;
